import { Link, useParams } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import type { BaselineDeliverable, BaselineEpic, BaselineStory } from "@/hooks/use-baseline";
import { cn, titleCase } from "@/lib/utils";

interface BaselineTreeProps {
  deliverables: BaselineDeliverable[];
  selectedStoryId: string | null;
}

export function BaselineTree({ deliverables, selectedStoryId }: BaselineTreeProps) {
  const initiallyOpen = useMemo(() => {
    const open = new Set<string>();
    for (const d of deliverables) {
      open.add(d.id);
      for (const e of d.epics) open.add(e.id);
    }
    return open;
  }, [deliverables]);

  const [openIds, setOpenIds] = useState<Set<string>>(initiallyOpen);

  function toggle(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      {deliverables.map((d) => (
        <DeliverableNode
          key={d.id}
          deliverable={d}
          openIds={openIds}
          onToggle={toggle}
          selectedStoryId={selectedStoryId}
        />
      ))}
    </div>
  );
}

function DeliverableNode({
  deliverable,
  openIds,
  onToggle,
  selectedStoryId,
}: {
  deliverable: BaselineDeliverable;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  selectedStoryId: string | null;
}) {
  const open = openIds.has(deliverable.id);
  const storyCount = deliverable.epics.reduce((n, e) => n + e.stories.length, 0);

  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(deliverable.id)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          {deliverable.code}
        </span>
        <span className="font-display font-semibold text-foreground flex-1 truncate">
          {deliverable.title}
        </span>
        <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          {titleCase(deliverable.group)}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {storyCount} {storyCount === 1 ? "story" : "stories"}
        </span>
      </button>
      {open && (
        <div className="border-t border-[var(--color-border)] divide-y divide-[var(--color-border)]">
          {deliverable.epics.map((e) => (
            <EpicNode
              key={e.id}
              epic={e}
              isOpen={openIds.has(e.id)}
              onToggle={onToggle}
              selectedStoryId={selectedStoryId}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function EpicNode({
  epic,
  isOpen,
  onToggle,
  selectedStoryId,
}: {
  epic: BaselineEpic;
  isOpen: boolean;
  onToggle: (id: string) => void;
  selectedStoryId: string | null;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(epic.id)}
        className="w-full flex items-center gap-3 pl-10 pr-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="font-mono text-xs text-muted-foreground tabular-nums">{epic.code}</span>
        <span className="text-sm font-medium text-foreground flex-1 truncate">{epic.title}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{epic.stories.length}</span>
      </button>
      {isOpen && (
        <ul>
          {epic.stories.map((s) => (
            <StoryRow key={s.id} story={s} selectedStoryId={selectedStoryId} />
          ))}
        </ul>
      )}
    </div>
  );
}

function StoryRow({
  story,
  selectedStoryId,
}: {
  story: BaselineStory;
  selectedStoryId: string | null;
}) {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const isActive = selectedStoryId === story.id;
  // Tree should be a quick scan: short label only. Full story text lives
  // in the StoryDetailSheet — clicking the row opens it.
  const primary = story.label?.trim() || truncate(story.text, 90);

  return (
    <li>
      <Link
        to="/s/$solutionId/scope/$storyId"
        params={{ solutionId, storyId: story.id }}
        className={cn(
          "flex items-center gap-3 pl-16 pr-4 py-1.5 text-left transition-colors border-l-2",
          isActive ? "bg-nymbl-sky border-nymbl-blue" : "border-transparent hover:bg-muted/40",
        )}
      >
        <span className="font-mono text-[11px] text-muted-foreground tabular-nums shrink-0">
          {story.code}
        </span>
        <span className="text-sm text-foreground truncate min-w-0 flex-1">{primary}</span>
        {story.acceptanceCriteria.length > 0 && (
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground shrink-0">
            {story.acceptanceCriteria.length} AC
          </span>
        )}
      </Link>
    </li>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`;
}
